import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { useAuth } from "@/lib/auth";
import { applyMessage, applyRead, chatQueryKeys } from "@/hooks/useDealChat";
import { notificationQueryKeys } from "@/hooks/useNotifications";
import { SHARED_PROTECTED_ROUTES } from "@/routes/url";
import { chatSocketUrl } from "@/services/chat.service";
import { getAuthToken } from "@/utils/local-storage.utils";
import type { ChatInboxResponse, ChatMessage, ChatSocketEvent, ChatSocketStatus } from "@/types/chat.types";

const PING_INTERVAL_MS = 25_000;
const MAX_BACKOFF_MS = 30_000;
/** Server close codes that mean "don't come back": bad session, or refused. */
const FINAL_CLOSE_CODES = new Set([4401, 4403]);

// Outside a provider (a page rendered without the dashboard shell) the chat
// still works — it just polls, which is what "closed" tells the queries to do.
const ChatSocketContext = createContext<ChatSocketStatus>("closed");

export const useChatSocketStatus = () => useContext(ChatSocketContext);

const TOAST_PREVIEW_CHARS = 120;

// Conversations on screen right now, keyed `dealId:threadId`, counted in case
// two panes ever show the same one. A message into one of these needs no toast.
const onScreen = new Map<string, number>();

/** Marks a conversation as being read on screen while `active`. */
export function useChatThreadOnScreen(dealId: string, threadId: string, active: boolean) {
  useEffect(() => {
    if (!active) return undefined;
    const key = `${dealId}:${threadId}`;
    onScreen.set(key, (onScreen.get(key) || 0) + 1);
    return () => {
      const left = (onScreen.get(key) || 1) - 1;
      if (left) onScreen.set(key, left);
      else onScreen.delete(key);
    };
  }, [dealId, threadId, active]);
}

/**
 * One live connection per signed-in tab, covering every deal the user can
 * see. Mounted in the dashboard layout so the sidebar's Chats badge, every
 * deal's Chat tab badge and any open conversation update from the same stream.
 *
 * Every `ready` — the first and each reconnect — invalidates all chat queries.
 * That is the catch-up: anything sent while the socket was down (a laptop lid,
 * a train tunnel, a deploy) comes back through REST rather than being silently
 * missing from the thread.
 */
export function ChatSocketProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const viewerId = user?.id;
  const [status, setStatus] = useState<ChatSocketStatus>("connecting");
  const navigate = useNavigate();

  // Read through a ref so the socket effect doesn't reconnect on navigation.
  const announce = useRef<(message: ChatMessage) => void>(() => {});
  announce.current = (message) => {
    const inbox = queryClient.getQueryData<ChatInboxResponse>(chatQueryKeys.inbox());
    const address = inbox?.deals.find((d) => d.deal_id === message.deal_id)?.address;
    const body = message.body.length > TOAST_PREVIEW_CHARS
      ? `${message.body.slice(0, TOAST_PREVIEW_CHARS).trimEnd()}…`
      : message.body;
    toast(`New message from ${message.sender_name || "someone"}`, {
      // One toast per conversation: a burst of messages updates it in place.
      id: `chat:${message.deal_id}:${message.thread_id}`,
      description: address ? `${address} — ${body}` : body,
      action: {
        label: "Open",
        onClick: () => navigate(`${SHARED_PROTECTED_ROUTES.CHATS}?deal=${message.deal_id}`),
      },
    });
  };

  useEffect(() => {
    if (!viewerId) return undefined;
    let socket: WebSocket | null = null;
    let pingTimer: ReturnType<typeof setInterval> | undefined;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;
    let stopped = false;

    const clearTimers = () => {
      clearInterval(pingTimer);
      clearTimeout(retryTimer);
    };

    const connect = () => {
      clearTimers();
      const token = getAuthToken();
      if (!token) {
        setStatus("closed");
        return;
      }
      setStatus(attempts ? "reconnecting" : "connecting");
      const ws = new WebSocket(chatSocketUrl());
      socket = ws;
      ws.onopen = () => ws.send(JSON.stringify({ type: "auth", token }));
      ws.onmessage = (event) => {
        let data: ChatSocketEvent;
        try {
          data = JSON.parse(event.data);
        } catch {
          return;
        }
        if (data.type === "ready") {
          attempts = 0;
          setStatus("open");
          queryClient.invalidateQueries({ queryKey: chatQueryKeys.all() });
          pingTimer = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "ping" }));
          }, PING_INTERVAL_MS);
        } else if (data.type === "message") {
          applyMessage(queryClient, data.deal_id, data.message, viewerId);
          // `alert` means this person is on the other end of the message, not
          // just allowed to read it. The server wrote their bell nudge first.
          if (data.alert) {
            queryClient.invalidateQueries({ queryKey: notificationQueryKeys.all() });
            if (!onScreen.has(`${data.deal_id}:${data.thread_id}`)) announce.current(data.message);
          }
        } else if (data.type === "read") {
          applyRead(queryClient, data.deal_id, data.thread_id, data.last_read_at);
          // Reading a conversation clears its bell nudge server-side.
          queryClient.invalidateQueries({ queryKey: notificationQueryKeys.all() });
          toast.dismiss(`chat:${data.deal_id}:${data.thread_id}`);
        }
      };
      ws.onclose = (event) => {
        if (socket !== ws) return;
        clearInterval(pingTimer);
        socket = null;
        if (stopped) return;
        if (FINAL_CLOSE_CODES.has(event.code)) {
          setStatus("closed");
          return;
        }
        setStatus("reconnecting");
        // Exponential backoff with jitter, so a backend restart is not met by
        // every open tab reconnecting in the same millisecond.
        const delay = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** attempts) + Math.random() * 500;
        attempts += 1;
        retryTimer = setTimeout(connect, delay);
      };
    };

    // Coming back online or back to the tab should not wait out a 30s backoff.
    const reconnectNow = () => {
      if (stopped || socket) return;
      attempts = 0;
      connect();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") reconnectNow();
    };

    connect();
    window.addEventListener("online", reconnectNow);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stopped = true;
      clearTimers();
      window.removeEventListener("online", reconnectNow);
      document.removeEventListener("visibilitychange", onVisible);
      const ws = socket;
      socket = null;
      if (ws) {
        ws.onclose = null;
        ws.onmessage = null;
        // Closing a socket that is still CONNECTING logs a browser warning;
        // let it open and close it straight away instead.
        if (ws.readyState === WebSocket.CONNECTING) ws.onopen = () => ws.close(1000);
        else ws.close(1000);
      }
    };
  }, [viewerId, queryClient]);

  return <ChatSocketContext.Provider value={status}>{children}</ChatSocketContext.Provider>;
}
